<template>
  <div class="booking-calendar" :class="{ 'booking-calendar--rtl': isArabic }">
    <div class="desktop-calendar">
      <div class="calendar-header">
        <button class="nav-btn" type="button" @click="previousMonth">
          <v-icon small>{{ previousIcon }}</v-icon>
        </button>
        <h3 class="month-title">{{ currentMonth }} {{ currentYear }}</h3>
        <button class="nav-btn" type="button" @click="nextMonth">
          <v-icon small>{{ nextIcon }}</v-icon>
        </button>
      </div>

      <div class="calendar-grid">
        <div class="weekday-header">
          <div v-for="day in weekDays" :key="day" class="weekday">
            {{ day }}
          </div>
        </div>

        <div class="dates-grid">
          <button
            v-for="date in calendarDates"
            :key="date.key"
            class="date-btn"
            :class="{
              'date-other-month': !date.isCurrentMonth,
              'date-today': date.isToday,
              'date-selected': date.isSelected,
              'date-disabled': date.isDisabled,
            }"
            :disabled="date.isDisabled"
            type="button"
            @click="selectDate(date)"
          >
            {{ date.day }}
          </button>
        </div>
      </div>
    </div>

    <div class="mobile-calendar">
      <div class="mobile-calendar-header">
        <button
          class="scroll-btn scroll-btn--left"
          type="button"
          @click="scrollPrevious"
        >
          <v-icon small>{{ previousIcon }}</v-icon>
        </button>

        <h4 class="mobile-month-title">{{ currentMonth }} {{ currentYear }}</h4>

        <button
          class="scroll-btn scroll-btn--right"
          type="button"
          @click="scrollNext"
        >
          <v-icon small>{{ nextIcon }}</v-icon>
        </button>
      </div>

      <div class="horizontal-scroll">
        <div
          ref="scrollContainer"
          class="dates-scroll"
          :class="{ 'dates-scroll--dragging': dragState.isDragging }"
          @pointerdown="onDragStart"
          @pointermove="onDragMove"
          @pointerup="onDragEnd"
          @pointercancel="onDragEnd"
          @pointerleave="onDragEnd"
          @touchstart.passive="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd"
          @touchcancel="onTouchEnd"
        >
          <div class="dates-track">
            <button
              v-for="date in horizontalDates"
              :key="date.key"
              class="horizontal-date"
              :class="{
                'horizontal-date--selected': date.isSelected,
                'horizontal-date--disabled': date.isDisabled,
              }"
              :disabled="date.isDisabled"
              type="button"
              @click="selectDate(date, $event)"
              @dragstart.prevent
            >
              <div class="date-day">{{ date.dayName }}</div>
              <div class="date-number">{{ date.day }}</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "date-picker",
  data() {
    const today = new Date();
    return {
      selectedDate: today,
      currentDate: new Date(today.getFullYear(), today.getMonth(), 1),
      dragState: {
        isPointerDown: false,
        isDragging: false,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
        suppressClick: false,
      },
      touchState: {
        isTouching: false,
        startX: 0,
        startScrollLeft: 0,
      },
      suppressClickTimeout: null,
    };
  },
  computed: {
    isArabic() {
      return this.$i18n.locale === "ar";
    },
    localeCode() {
      return this.isArabic ? "ar-EG" : "en-US";
    },
    weekDays() {
      return [
        this.$t("weekDaysText.Sunday"),
        this.$t("weekDaysText.Monday"),
        this.$t("weekDaysText.Tuesday"),
        this.$t("weekDaysText.Wednesday"),
        this.$t("weekDaysText.Thursday"),
        this.$t("weekDaysText.Friday"),
        this.$t("weekDaysText.Saturday"),
      ];
    },
    currentMonth() {
      return this.currentDate.toLocaleDateString(this.localeCode, {
        month: "long",
      });
    },
    currentYear() {
      return this.currentDate.getFullYear();
    },
    previousIcon() {
      return this.isArabic ? "mdi-arrow-right" : "mdi-arrow-left";
    },
    nextIcon() {
      return this.isArabic ? "mdi-arrow-left" : "mdi-arrow-right";
    },
    calendarDates() {
      const dates = [];
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());

      for (let i = 0; i < 42; i += 1) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);

        dates.push({
          key: date.toISOString(),
          day: date.getDate(),
          dayName: this.getShortDayName(date),
          date,
          isCurrentMonth: date.getMonth() === month,
          isToday: this.isSameDay(date, new Date()),
          isSelected: this.selectedDate
            ? this.isSameDay(date, this.selectedDate)
            : false,
          isDisabled: this.isPastDate(date),
        });
      }

      return dates;
    },
    horizontalDates() {
      const dates = [];
      const today = new Date();

      for (let i = 0; i < 30; i += 1) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);

        dates.push({
          key: `${date.toISOString()}-${i}`,
          day: date.getDate(),
          dayName: this.getShortDayName(date),
          date,
          isCurrentMonth: date.getMonth() === this.currentDate.getMonth(),
          isToday: i === 0,
          isSelected: this.selectedDate
            ? this.isSameDay(date, this.selectedDate)
            : false,
          isDisabled: false,
        });
      }

      return dates;
    },
  },
  mounted() {
    this.emitDate(this.selectedDate);
    this.$nextTick(() => {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.addEventListener(
          "scroll",
          this.updateMonthFromScroll
        );
      }
    });
  },
  beforeDestroy() {
    if (this.$refs.scrollContainer) {
      this.$refs.scrollContainer.removeEventListener(
        "scroll",
        this.updateMonthFromScroll
      );
    }
    if (this.suppressClickTimeout) {
      clearTimeout(this.suppressClickTimeout);
    }
  },
  methods: {
    getShortDayName(date) {
      const dayMap = [
        this.$t("weekDaysText.Sunday"),
        this.$t("weekDaysText.Monday"),
        this.$t("weekDaysText.Tuesday"),
        this.$t("weekDaysText.Wednesday"),
        this.$t("weekDaysText.Thursday"),
        this.$t("weekDaysText.Friday"),
        this.$t("weekDaysText.Saturday"),
      ];
      return dayMap[date.getDay()];
    },
    isSameDay(date1, date2) {
      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      );
    },
    isPastDate(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },
    emitDate(date) {
      this.$emit("input", date.toISOString().split("T")[0]);
    },
    selectDate(dateItem, event) {
      if (event && this.dragState.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (dateItem.isDisabled) return;

      const currentScrollLeft = this.$refs.scrollContainer
        ? this.$refs.scrollContainer.scrollLeft
        : 0;
      this.selectedDate = new Date(dateItem.date);
      this.currentDate = new Date(
        dateItem.date.getFullYear(),
        dateItem.date.getMonth(),
        1
      );
      this.emitDate(this.selectedDate);

      this.$nextTick(() => {
        if (this.$refs.scrollContainer) {
          this.$refs.scrollContainer.scrollLeft = currentScrollLeft;
        }
      });
    },
    previousMonth() {
      const nextDate = new Date(this.currentDate);
      nextDate.setMonth(nextDate.getMonth() - 1);
      this.currentDate = nextDate;
    },
    nextMonth() {
      const nextDate = new Date(this.currentDate);
      nextDate.setMonth(nextDate.getMonth() + 1);
      this.currentDate = nextDate;
    },
    scrollPrevious() {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollBy({ left: -240, behavior: "smooth" });
      }
    },
    scrollNext() {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollBy({ left: 240, behavior: "smooth" });
      }
    },
    onDragStart(event) {
      if (!this.$refs.scrollContainer) return;

      this.dragState.isPointerDown = true;
      this.dragState.isDragging = false;
      this.dragState.pointerId = event.pointerId;
      this.dragState.startX = event.clientX;
      this.dragState.startScrollLeft = this.$refs.scrollContainer.scrollLeft;
      this.dragState.suppressClick = false;

      if (this.$refs.scrollContainer.setPointerCapture) {
        this.$refs.scrollContainer.setPointerCapture(event.pointerId);
      }
    },
    onDragMove(event) {
      if (!this.dragState.isPointerDown || !this.$refs.scrollContainer) return;

      const deltaX = event.clientX - this.dragState.startX;

      if (!this.dragState.isDragging && Math.abs(deltaX) > 6) {
        this.dragState.isDragging = true;
        this.dragState.suppressClick = true;
      }

      if (!this.dragState.isDragging) return;

      this.$refs.scrollContainer.scrollLeft =
        this.dragState.startScrollLeft - deltaX;
    },
    onDragEnd() {
      if (!this.dragState.isPointerDown || !this.$refs.scrollContainer) return;

      if (
        this.dragState.pointerId !== null &&
        this.$refs.scrollContainer.releasePointerCapture
      ) {
        try {
          this.$refs.scrollContainer.releasePointerCapture(
            this.dragState.pointerId
          );
        } catch (error) {
          // Ignore release failures when the pointer is already gone.
        }
      }

      this.dragState.isPointerDown = false;
      this.dragState.pointerId = null;
      this.dragState.startX = 0;
      this.dragState.startScrollLeft = this.$refs.scrollContainer.scrollLeft;

      if (!this.dragState.isDragging) return;

      this.dragState.isDragging = false;
      if (this.suppressClickTimeout) {
        clearTimeout(this.suppressClickTimeout);
      }
      this.suppressClickTimeout = setTimeout(() => {
        this.dragState.suppressClick = false;
      }, 0);
    },
    onTouchStart(event) {
      if (!this.$refs.scrollContainer || !event.touches.length) return;

      this.touchState.isTouching = true;
      this.touchState.startX = event.touches[0].clientX;
      this.touchState.startScrollLeft = this.$refs.scrollContainer.scrollLeft;
      this.dragState.isDragging = false;
      this.dragState.suppressClick = false;
    },
    onTouchMove(event) {
      if (
        !this.touchState.isTouching ||
        !this.$refs.scrollContainer ||
        !event.touches.length
      ) {
        return;
      }

      const deltaX = event.touches[0].clientX - this.touchState.startX;

      if (!this.dragState.isDragging && Math.abs(deltaX) > 6) {
        this.dragState.isDragging = true;
        this.dragState.suppressClick = true;
      }

      if (!this.dragState.isDragging) return;

      event.preventDefault();
      this.$refs.scrollContainer.scrollLeft =
        this.touchState.startScrollLeft - deltaX;
    },
    onTouchEnd() {
      if (!this.touchState.isTouching) return;

      this.touchState.isTouching = false;
      this.touchState.startX = 0;
      this.touchState.startScrollLeft = this.$refs.scrollContainer
        ? this.$refs.scrollContainer.scrollLeft
        : 0;

      if (!this.dragState.isDragging) return;

      this.dragState.isDragging = false;
      if (this.suppressClickTimeout) {
        clearTimeout(this.suppressClickTimeout);
      }
      this.suppressClickTimeout = setTimeout(() => {
        this.dragState.suppressClick = false;
      }, 0);
    },
    updateMonthFromScroll() {
      if (!this.$refs.scrollContainer) return;

      const itemWidth = 82;
      const visibleIndex = Math.round(
        this.$refs.scrollContainer.scrollLeft / itemWidth
      );
      if (!this.horizontalDates[visibleIndex]) return;

      const visibleDate = this.horizontalDates[visibleIndex].date;
      this.currentDate = new Date(
        visibleDate.getFullYear(),
        visibleDate.getMonth(),
        1
      );
    },
  },
};
</script>

<style scoped lang="scss">
.booking-calendar {
  height: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.desktop-calendar {
  display: block;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.booking-calendar--rtl .calendar-header {
  direction: rtl;
}

.nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 999px;
  background: #f8fafc;
  transition: background 0.2s ease;
}

.nav-btn:hover {
  background: #eef2f7;
}

.month-title,
.mobile-month-title {
  margin: 0;
  color: var(--main-color);
  font-size: 20px;
  font-weight: 800;
}

.calendar-grid {
  padding: 18px;
}

.weekday-header,
.dates-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
}

.weekday {
  padding: 8px 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
}

.date-btn {
  width: 100%;
  min-height: 48px;
  border: none;
  border-radius: 16px;
  color: var(--main-color);
  font-size: 15px;
  font-weight: 700;
  background: transparent;
  transition: all 0.18s ease;
}

.date-btn:hover:not(:disabled) {
  background: #f8fafc;
}

.date-other-month {
  color: #cbd5e1;
}

.date-today {
  background: rgba(15, 23, 42, 0.05);
}

.date-selected {
  color: #fff;
  background: linear-gradient(
    180deg,
    var(--main-color),
    var(--secondary-color)
  );
  box-shadow: 0 16px 26px rgba(15, 23, 42, 0.12);
}

.date-disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.mobile-calendar {
  display: none;
  width: 100%;
  min-width: 0;
}

.mobile-calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 16px 10px;
}

.horizontal-scroll {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.scroll-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border: none;
  border-radius: 999px;
  background: rgba(248, 250, 252, 0.96);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}

.dates-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 8px 16px 14px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  cursor: grab;
  user-select: none;
  box-sizing: border-box;
}

.dates-scroll::-webkit-scrollbar {
  display: none;
}

.dates-track {
  display: flex;
  gap: 10px;
  width: max-content;
  min-width: max-content;
  padding-inline-end: 2px;
}

.dates-scroll--dragging {
  cursor: grabbing;
}

.horizontal-date {
  flex: 0 0 72px;
  width: 72px;
  max-width: 72px;
  min-height: 88px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  background: #fff;
  scroll-snap-align: start;
  -webkit-user-select: none;
  user-select: none;
}

.horizontal-date--selected {
  color: #fff;
  background: linear-gradient(
    180deg,
    var(--main-color),
    var(--secondary-color)
  );
  border-color: transparent;
}

.horizontal-date--disabled {
  opacity: 0.45;
}

.date-day {
  padding-top: 14px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
}

.date-number {
  padding-top: 8px;
  font-size: 22px;
  font-weight: 800;
  color: var(--main-color);
}

.horizontal-date--selected .date-day,
.horizontal-date--selected .date-number {
  color: #fff;
}

@media (max-width: 959px) {
  .desktop-calendar {
    display: none;
  }

  .mobile-calendar {
    display: block;
    padding: 18px 0 8px;
    width: 100%;
    min-width: 0;
  }

  .mobile-month-title {
    text-align: center;
    font-size: 18px;
    flex: 1 1 auto;
  }
}
</style>
